import { GetRowIdParams, GridApi, GridReadyEvent, RowSelectedEvent } from 'ag-grid-community';
import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { TableRowWrapper, TableWrapper } from '../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import Button from '../../../components/Button/Button';
import { EditCellRenderer } from '../../../components/Table/EditCellRenderer/EditCellRenderer';
import Table from '../../../components/Table/Table';
import { RawProd, SubAssembly } from '../../../services/rawProd/rawProd.types';
import { ButtonRow, Page } from './EditSubassembly.styles';
import { EditSubassemblyProps } from './EditSubassembly.types';
import RawProductSelectTable from "../../../components/RawProduct/RawProductSelectTable";
import { AgGridReact } from "ag-grid-react";

const EditSubassembly: FC<EditSubassemblyProps> = (
    {
      setSelectedSubassemblyRawProds,
      selectedSubassemblyRawProds,
      setEditSubassemblyActive,
      assembledRawProdNo
    }
) => {
  const [selectedGridApi, setSelectedGridApi] = useState<GridApi | undefined>(undefined);
  const availableRawProductGridRef = useRef<AgGridReact>();
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (inputError) setInputError(false);
    setInputValue(event.target.value);
  };

  const handleAddRawProd = () => {
    const rawProd: RawProd | undefined =
        availableRawProductGridRef.current?.api.getRowNode(inputValue)?.data;

    if (!rawProd) {
      setInputError(true);
    } else {
      const alreadySelected = selectedSubassemblyRawProds.find(
        (subassembly) => subassembly.individualMoaRawProdNo === rawProd.moaRawProdNo
      );
      if (alreadySelected) {
        return;
      } else {
        setInputValue('');
        setSelectedSubassemblyRawProds((selectedSubassemblyRawProds) => [
          ...selectedSubassemblyRawProds,
          {
            assembledMoaRawProdNo: +assembledRawProdNo,
            individualMoaRawProdNo: rawProd.moaRawProdNo,
            usageAmount: 0,
            description: rawProd.moaRawProdDesc,
          },
        ]);
      }
    }
  };

  const handleSaveClick = () => {
    setEditSubassemblyActive(false);
  };

  const handleRowSelectedAvailable = (event: RowSelectedEvent) => {
    const alreadySelected =
      selectedSubassemblyRawProds.findIndex(
        (subassembly) => subassembly.individualMoaRawProdNo === event.data.moaRawProdNo
      ) !== -1;

    // row has been checked, add to selectedSubassemblyRawProds
    if (event.node.isSelected() && !alreadySelected) {
      setSelectedSubassemblyRawProds((selectedSubassemblyRawProds) => [
        ...selectedSubassemblyRawProds,
        {
          assembledMoaRawProdNo: +assembledRawProdNo,
          individualMoaRawProdNo: event.data.moaRawProdNo,
          usageAmount: 0,
          description: event.data.moaRawProdDesc,
        },
      ]);
    }
    // row has been unchecked, remove from selectedSubassemblyRawProds
    if (!event.node.isSelected() && alreadySelected) {
      setSelectedSubassemblyRawProds((selectedSubassemblyRawProds) =>
        selectedSubassemblyRawProds.filter(
          (subassembly) => subassembly.individualMoaRawProdNo !== event.data.moaRawProdNo
        )
      );
    }
  };

  const handleRowSelectedSelected = (event: RowSelectedEvent) => {
    const selectedRows = event.api.getSelectedRows();
    if (availableRawProductGridRef) {
      availableRawProductGridRef.current?.api.getRowNode(event.data.individualMoaRawProdNo)?.setSelected(event.node.isSelected() || false);
    }
    setSelectedSubassemblyRawProds(() => selectedRows);
  };

  useEffect(() => {
    if (availableRawProductGridRef && selectedGridApi && selectedSubassemblyRawProds) {
      selectedGridApi.selectAll();
      selectedSubassemblyRawProds.forEach((subAssembly) => {
        const node = availableRawProductGridRef.current?.api.getRowNode(subAssembly.individualMoaRawProdNo?.toString());
        if (node) node.setSelected(true);
      });
    }
  }, [availableRawProductGridRef, selectedSubassemblyRawProds, selectedGridApi]);
  const handleCellEdit = useCallback(
    (event: any) => { // Using any for CellEditRequest event
      let newValue = +event.newValue;
      if (newValue > 9999.9999) newValue = 9999.9999;
      if (newValue < -9999.9999) newValue = -9999.9999;
      if (Number.isNaN(newValue)) newValue = event.oldValue;
      setSelectedSubassemblyRawProds((selectedSubassemblyRawProds) =>
        selectedSubassemblyRawProds.map((subassembly: SubAssembly) => {
          if (subassembly.individualMoaRawProdNo === event.data.individualMoaRawProdNo) {
            return {
              ...subassembly,
              usageAmount: newValue,
            };
          } else {
            return subassembly;
          }
        })
      );
    },
    [selectedSubassemblyRawProds]
  );

  return (
    <Page data-testid='EditSubassembly'>
      <ButtonRow>
        <Button label='Save' onClick={handleSaveClick} />
      </ButtonRow>
      <TableRowWrapper>
        <RawProductSelectTable
            ref={availableRawProductGridRef}
            title={'Available Raw Products'}
            onSelected={handleRowSelectedAvailable}
            rowSelection='multiple'
            getRowId={(params) => params.data.moaRawProdNo?.toString()}
        />
        <TableWrapper>
          <Table
            height='70vh'
            showHeader
            headerTitle='Selected Raw Products'
            onCellEditRequest={handleCellEdit}
            rowSelection='multiple'
            rowData={selectedSubassemblyRawProds}
            returnedRecords={selectedSubassemblyRawProds.length}
            onRowSelected={handleRowSelectedSelected}
            onGridReady={(event: GridReadyEvent) => setSelectedGridApi(event.api)}
            getRowId={(params: GetRowIdParams) => params.data.individualMoaRawProdNo?.toString()}
            readOnlyEdit={true}
            suppressRowClickSelection={true}
            columnDefs={[
              {
                minWidth: 70,
                maxWidth: 70,
                flex: 1,
                headerName: 'Select',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              { minWidth: 120, flex: 1, field: 'individualMoaRawProdNo', headerName: 'Raw Product' },
              { minWidth: 220, flex: 2, field: 'description', headerName: 'Description' },
              {
                minWidth: 120,
                flex: 1,
                field: 'usageAmount',
                headerName: 'Usage Amount',
                editable: true,
                resizable: false,
                cellRenderer: EditCellRenderer,
              },
            ]}
            headerButtons={[
              {
                inputProps: {
                  label: '',
                  id: 'addRawProd',
                  errorText: inputError ? 'Raw product not found' : '',
                  onChange: handleChange,
                  value: inputValue,
                  'aria-label': 'select raw product',
                },
                buttonProps: {
                  label: 'Add Raw Product',
                  onClick: handleAddRawProd,
                },
              },
            ]}
          />
        </TableWrapper>
      </TableRowWrapper>
    </Page>
  );
};

export default EditSubassembly;
